import { getDefaultNodeTitle } from "@kuika/kuika-cl-model";
import { Edge, Node, useReactFlow } from "reactflow";

let nodeIdCounter = 1000; // Start from 1000 to avoid conflicts with initial nodes
const generateNodeId = () => `node_${nodeIdCounter++}`;

// Callback type for taking snapshots (optional)
type TakeSnapshotCallback = (nodes: Node[], edges: Edge[]) => void;

// Store the takeSnapshot callback at module level so all hooks can access it
let globalTakeSnapshot: TakeSnapshotCallback | null = null;

/**
 * Set the global takeSnapshot callback. Called once by WorkflowBuilder.
 */
export const setHistoryCallback = (callback: TakeSnapshotCallback | null) => {
  globalTakeSnapshot = callback;
};

/**
 * Custom hook to handle programmatic node creation from ExtendingConnector
 * Creates a new node and automatically connects it to the source node
 */
export const useNodeCreation = (sourceNodeId: string) => {
  const { setNodes, setEdges, getNode, getNodes, getEdges } = useReactFlow();

  const createConnectedNode = (nodeType: string) => {
    const sourceNode = getNode(sourceNodeId);
    if (!sourceNode) {
      console.error("Source node not found:", sourceNodeId);
      return;
    }

    // Take snapshot before creating node (for undo)
    if (globalTakeSnapshot) {
      globalTakeSnapshot(getNodes(), getEdges());
    }

    // Calculate position for new node (to the right of source node)
    const HORIZONTAL_OFFSET = 300; // Distance to the right
    const newNodePosition = {
      x: sourceNode.position.x + HORIZONTAL_OFFSET,
      y: sourceNode.position.y
    };

    // Create the new node
    const newNodeId = generateNodeId();
    const newNode: Node = {
      id: newNodeId,
      type: nodeType,
      position: newNodePosition,
      data: {
        label: `${nodeType} node`,
        title: getDefaultNodeTitle(nodeType),
        type: nodeType // Add type to data for ProcessorNode and other nodes that need it
      }
    };

    // Add the node
    setNodes((nodes) => [...nodes, newNode]);

    // Create edge connecting source to new node
    setEdges((edges) => [
      ...edges,
      {
        id: `edge-${sourceNodeId}-to-${newNodeId}`,
        source: sourceNodeId,
        target: newNodeId
      }
    ]);
  };

  return { createConnectedNode };
};
